import { useState, useCallback } from 'react';
import useFreeTranslation from './useFreeTranslation';
import {
  countryToSourceLanguage,
  skuTranslationKey,
} from '../utils/skuTitleTranslationUtils';

export function useSkuTitleTranslations() {
  const { translateText } = useFreeTranslation();
  const [translations, setTranslations] = useState({});
  const [activeKeys, setActiveKeys] = useState(() => new Set());
  const [loadingKeys, setLoadingKeys] = useState(() => new Set());

  const getDisplayTitle = useCallback(
    (title, country) => {
      if (!title) return title;
      const key = skuTranslationKey(title, country);
      if (!activeKeys.has(key)) return title;
      return translations[key] ?? title;
    },
    [activeKeys, translations]
  );

  const isTranslationActive = useCallback(
    (title, country) => activeKeys.has(skuTranslationKey(title, country)),
    [activeKeys]
  );

  const isTranslatingTitle = useCallback(
    (title, country) => loadingKeys.has(skuTranslationKey(title, country)),
    [loadingKeys]
  );

  const toggleSkuTitleTranslation = useCallback(
    async (title, country) => {
      if (!title) return;
      const key = skuTranslationKey(title, country);

      if (activeKeys.has(key)) {
        setActiveKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        return;
      }

      const sourceLang = countryToSourceLanguage(country);
      if (sourceLang === 'en') {
        setTranslations((prev) => ({ ...prev, [key]: title }));
        setActiveKeys((prev) => new Set(prev).add(key));
        return;
      }

      if (translations[key]) {
        setActiveKeys((prev) => new Set(prev).add(key));
        return;
      }

      setLoadingKeys((prev) => new Set(prev).add(key));
      try {
        const english = await translateText(title, 'en', sourceLang);
        setTranslations((prev) => ({ ...prev, [key]: english || title }));
        setActiveKeys((prev) => new Set(prev).add(key));
      } finally {
        setLoadingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [activeKeys, translations, translateText]
  );

  return {
    getDisplayTitle,
    isTranslationActive,
    isTranslatingTitle,
    toggleSkuTitleTranslation,
  };
}

export default useSkuTitleTranslations;
